import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * MFA Challenge Screen
 *
 * Shown immediately after a user submits the right password if their
 * account has MFA enabled. The login route returned a short-lived signed
 * `challenge_token` that proves the password step succeeded; we POST it
 * here alongside the 6-digit TOTP code (or one of the 10-character
 * recovery codes) to complete login.
 *
 * Routing: navigate('/mfa-challenge', { state: { challenge_token, email } })
 */
export default function MfaChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usingRecovery, setUsingRecovery] = useState(false);
  const inputRef = useRef(null);

  const challengeToken = location.state?.challenge_token;
  const email = location.state?.email;
  const returnTo = location.state?.return_to;

  useEffect(() => {
    // If the user navigates here directly without a challenge_token,
    // bounce them back to login — there is nothing to verify against.
    if (!challengeToken) {
      navigate('/login', { replace: true });
      return;
    }
    if (inputRef.current) inputRef.current.focus();
  }, [challengeToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmedCode = code.trim().replace(/\s+/g, '');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ challenge_token: challengeToken, code: trimmedCode }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Verification failed');
        setLoading(false);
        setCode('');
        return;
      }
      // Session created — return to the SSO origin if provided, else SPA root.
      window.location.href = returnTo || '/';
    } catch (err) {
      setError('Unable to connect. Please try again.');
      setLoading(false);
    }
  };

  if (!challengeToken) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/4 left-1/3 w-[600px] h-[460px] opacity-40"
          style={{ background: 'radial-gradient(ellipse, rgba(59,130,246,0.18) 0%, transparent 70%)', filter: 'blur(70px)' }}
        />
        <div
          className="absolute bottom-1/4 right-1/3 w-[520px] h-[380px] opacity-40"
          style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.14) 0%, transparent 70%)', filter: 'blur(70px)' }}
        />
      </div>

      <div className="relative z-10 w-full max-w-sm px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-blue-500/20 mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Verification required</h1>
          <p className="text-slate-600 text-sm mt-2">
            {usingRecovery
              ? 'Enter one of your recovery codes.'
              : 'Open your authenticator app and enter the current 6-digit code.'}
          </p>
          {email && <p className="text-slate-500 text-xs mt-1">{email}</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="mfa-code" className="block text-sm font-medium text-slate-700 mb-1">
                {usingRecovery ? 'Recovery code' : '6-digit code'}
              </label>
              <input
                ref={inputRef}
                id="mfa-code"
                type="text"
                inputMode={usingRecovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={usingRecovery ? 'XXXX-XXXX-XX' : '123456'}
                maxLength={usingRecovery ? 12 : 6}
                className="w-full px-3 py-2.5 text-lg tracking-widest text-center font-mono border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !code.trim()}
              className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white transition-all ${
                loading || !code.trim()
                  ? 'bg-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-md'
              }`}
            >
              {loading ? 'Verifying...' : 'Verify and continue'}
            </button>
          </form>

          <div className="mt-4 pt-4 border-t border-slate-100 text-center">
            <button
              type="button"
              onClick={() => {
                setUsingRecovery((v) => !v);
                setCode('');
                setError('');
                if (inputRef.current) inputRef.current.focus();
              }}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              {usingRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
            </button>
          </div>

          <div className="mt-2 text-center">
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
